class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  onClick(event) {
    const galleryButton = event.target.closest('[data-upsell-gallery-thumb]');
    if (galleryButton) {
      this.showGalleryImage(Number(galleryButton.dataset.galleryIndex));
      return;
    }

    const openButton = event.target.closest('[data-upsell-open]');
    if (openButton) {
      this.openQuickView(openButton);
      return;
    }

    if (event.target.closest('[data-upsell-close]')) {
      this.closeQuickView();
      return;
    }

    const button = event.target.closest('[data-upsell-add]');
    if (button) this.addToCart(button);
  }

  onKeyDown(event) {
    const quickView = this.querySelector('[data-upsell-quickview]');
    if (!quickView || quickView.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeQuickView();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      quickView.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hidden && element.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  openQuickView(trigger) {
    const quickView = this.querySelector('[data-upsell-quickview]');
    if (!quickView) return;

    clearTimeout(this.quickViewCloseTimer);
    this.quickViewTrigger = trigger;
    quickView.hidden = false;
    quickView.setAttribute('aria-hidden', 'false');
    this.showGalleryImage(0);

    requestAnimationFrame(() => {
      quickView.classList.add('is-open');
      quickView.querySelector('[data-upsell-close]:not([tabindex="-1"])')?.focus();
    });
  }

  closeQuickView() {
    const quickView = this.querySelector('[data-upsell-quickview]');
    if (!quickView || quickView.hidden) return;

    quickView.classList.remove('is-open');
    quickView.setAttribute('aria-hidden', 'true');

    this.quickViewCloseTimer = setTimeout(() => {
      quickView.hidden = true;
    }, 220);

    this.quickViewTrigger?.focus();
  }

  showGalleryImage(index) {
    const images = Array.from(this.querySelectorAll('[data-upsell-gallery-image]'));
    const thumbnails = Array.from(this.querySelectorAll('[data-upsell-gallery-thumb]'));
    if (!images.length) return;

    const safeIndex = Math.max(0, Math.min(index, images.length - 1));
    images.forEach((image, imageIndex) => {
      image.hidden = imageIndex !== safeIndex;
    });
    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.setAttribute('aria-current', thumbnailIndex === safeIndex ? 'true' : 'false');
    });
  }

  async addToCart(button) {
    if (button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    const relatedButtons = this.querySelectorAll(`[data-upsell-add][data-variant-id="${variantId}"]`);
    const error = button.closest('[data-upsell-quickview]')?.querySelector('[data-upsell-error]') ||
      this.querySelector('[data-upsell-error]');

    relatedButtons.forEach((relatedButton) => {
      relatedButton.disabled = true;
      relatedButton.classList.add('is-loading');
    });
    error.hidden = true;

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          updates: { [variantId]: 1 },
          discount: button.dataset.discountCode,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || window.cartStrings.error);
      }

      cartDrawer.renderContents(state);
    } catch (exception) {
      error.textContent = exception.message || window.cartStrings.error;
      error.hidden = false;
      relatedButtons.forEach((relatedButton) => {
        relatedButton.disabled = false;
        relatedButton.classList.remove('is-loading');
      });
    }
  }
}

customElements.define('cart-upsells', CartUpsells);
